import { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuthUser } from "../../contexts/AuthUserContext";
import SystemConfigurationTab from "../../components/admin/advanced/SystemConfigurationTab";
import ApiManagementTab from "../../components/admin/advanced/ApiManagementTab";
import SystemHealthTab from "../../components/admin/advanced/SystemHealthTab";
import SecurityTab from "../../components/admin/advanced/SecurityTab";
import AuditLogTab from "../../components/admin/advanced/AuditLogTab";

const AdminAdvanced = () => {
    const user = useAuthUser();
    const [activeTab, setActiveTab] = useState('system');

    const tabs = [
        { id: 'system', label: 'Configuration', icon: 'mdi:cog' },
        { id: 'api', label: 'API Management', icon: 'material-symbols:api-rounded' },
        { id: 'database', label: 'System Health', icon: 'mdi:heart-pulse' },
        { id: 'security', label: 'Security', icon: 'mdi:shield-check' },
        { id: 'audit', label: 'Audit Logs', icon: 'mdi:file-document-multiple' }
    ];

    const [animationStates, setAnimationStates] = useState({
        header: false,
        tabs: false,
        content: false,
        // Add more sections as needed: cards, form, grid, etc.
    });


    const renderTabContent = () => {
        switch (activeTab) {
            case 'system':
                return <SystemConfigurationTab />;

            case 'api':
                return <ApiManagementTab />;

            case 'database':
                return <SystemHealthTab />;

            case 'security':
                return <SecurityTab />;

            case 'audit':
                return <AuditLogTab />;

            default:
                return null;
        }
    };

    useEffect(() => {
        const timeouts = [];
        
        // Stagger the animations with delays - customize timing as needed
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));
        
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, tabs: true }));
        }, 300));
        
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, content: true }));
        }, 500));
    
        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);


    return (
        <div className="responsive-page-layout scrollable w-full">
            <div className={`flex flex-row justify-between items-center w-full transition-all duration-700 ease-out-in ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-4'
            }`}>
                <div className="header-textbox">
                    <Icon icon="rivet-icons:gears" className="header-icon" />
                    Advanced Administration
                </div>
            </div>

            <div className={`flex flex-col gap-4 h-full w-full`}>
                {/* Sidebar */}
                <div className={`flex gap-2 w-full flex-shrink-0 ${
                    animationStates.tabs
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                }`}>
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`
                                tickets-tab-responsive flex flex-col lg:flex-row gap-2 items-center justify-center p-2.5 truncate
                                ${activeTab === tab.id
                                    ? 'tickets-tab-active'
                                    : 'text-font-secondary border-b border-border group group-hover:text-font-primary'
                                }
                            `}
                        >
                            <Icon icon={tab.icon} className={`
                                text-xl
                                ${activeTab === tab.id
                                    ? 'text-primary'
                                    : 'text-font-secondary'
                                }`} />
                            <span className="hidden md:inline">{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className={`flex-1 px-2 pb-4 transition-all duration-1000 ease-out-in ${
                    animationStates.content
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                }`}>
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};

export default AdminAdvanced;